import jobServices from 'ApiService/Admin/jobServices';
import branchService from 'ApiService/SuperAdmin/branchService';
import TextField from 'components/fields/TextField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useContext, useState } from 'react';
import toast from 'react-hot-toast';

const PlacementStatusButton = ({
  student,
  loadingStatus,
  isPlacementPausedOverall,
  handleUnpausePlacement,
  setSelectedStudent,
  setShowPauseModal,
  pauseInfo,
  status,
  getStudents,
}) => {
  const { state } = useContext(AuthContext);
  // Super admins unpause directly instead of raising a request
  const isSuperAdmin = state?.user?.role === 'superadmin';
  // Admins can resume auto-declined pauses without approval
  const canUnpauseDirectly =
    isSuperAdmin || !!pauseInfo?.reason?.startsWith('Auto Declined');
  const [showUnpauseModal, setShowUnpauseModal] = useState(false);
  const [unpauseReason, setUnpauseReason] = useState('');
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const prepareUnpauseRequest = () => {
    if (!unpauseReason.trim()) {
      toast.error('Please provide a reason for unpausing placement');
      return;
    }
    setShowConfirmation(true);
  };

  const submitUnpauseRequest = async () => {
    setConfirmationLoading(true);

    if (!unpauseReason.trim()) {
      toast.error('Please provide a reason for unpausing placement');
      setConfirmationLoading(false);
      return;
    }

    try {
      const response = isSuperAdmin
        ? await branchService.unpausePlacement(student.id, unpauseReason.trim())
        : canUnpauseDirectly
        ? await jobServices.unpausePlacement(student.id, unpauseReason.trim())
        : await jobServices.requestUnpausePlacement(
            student.id,
            unpauseReason.trim()
          );

      if (response.data.success) {
        toast.success(
          response.data.message || 'Unpause request submitted successfully'
        );
        closeUnpauseModal();
        getStudents(); // Call getStudents to refresh data
      } else {
        toast.error(
          response.data.message || 'Failed to submit unpause request'
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to submit unpause request'
      );
    } finally {
      setConfirmationLoading(false);
      setShowConfirmation(false);
    }
  };

  const closeUnpauseModal = () => {
    setShowUnpauseModal(false);
    setUnpauseReason('');
    setShowConfirmation(false);
  };

  const isPermanentlyPaused = pauseInfo?.isPermanent;
  const currentStatus = status || 'none';

  if (isPermanentlyPaused) {
    return (
      <div>
        <button
          disabled={true}
          className="relative min-h-8 min-w-32 cursor-not-allowed rounded bg-red-500 px-3 py-1 text-white opacity-75"
        >
          Permanently Paused
        </button>
      </div>
    );
  }

  if (isPlacementPausedOverall) {
    return (
      <>
        <div className="flex flex-col items-center space-y-2">
          {pauseInfo?.startDate && pauseInfo?.endDate && (
            <div className="text-xs text-gray-600">
              Paused: {new Date(pauseInfo.startDate).toLocaleDateString()} to{' '}
              {new Date(pauseInfo.endDate).toLocaleDateString()}
            </div>
          )}
          {pauseInfo?.reason && (
            <div className="text-xs italic text-gray-600">
              Reason: {pauseInfo.reason}
            </div>
          )}

          {canUnpauseDirectly ? (
            <button
              onClick={() => setShowUnpauseModal(true)}
              disabled={loadingStatus}
              className="relative min-h-8 min-w-32 rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
            >
              {loadingStatus ? 'Loading..' : 'Unpause Placement'}
            </button>
          ) : currentStatus === 'pending' ? (
            <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
              Pending Approval
            </span>
          ) : currentStatus === 'approved' ? (
            <button
              onClick={() => setShowUnpauseModal(true)}
              disabled={loadingStatus}
              className="relative min-h-8 min-w-32 rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
            >
              {loadingStatus ? 'Loading..' : 'Unpause Placement'}
            </button>
          ) : currentStatus === 'rejected' ? (
            <button
              onClick={() => setShowUnpauseModal(true)}
              className="relative min-h-8 min-w-32 rounded bg-red-500 px-3 py-1 text-white hover:bg-red-600"
            >
              Retry Unpause
            </button>
          ) : (
            <button
              onClick={() => setShowUnpauseModal(true)}
              className="relative min-h-8 min-w-32 rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
            >
              Request Unpause
            </button>
          )}
        </div>

        {showUnpauseModal && (
          <div className="bg-black fixed inset-0 flex items-center justify-center bg-opacity-50 p-2">
            <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
              <button
                onClick={closeUnpauseModal}
                className="absolute right-2 top-2 text-2xl text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
              <h2 className="mb-4 text-lg font-semibold">
                {canUnpauseDirectly ? 'Unpause' : 'Request Unpause for'}{' '}
                {student?.name}
              </h2>

              <TextField
                label="Reason for Unpause"
                value={unpauseReason}
                onChange={(e) => setUnpauseReason(e.target.value)}
                placeholder="Enter reason for unpausing"
              />

              <div className="mt-5 flex justify-end">
                <button
                  onClick={prepareUnpauseRequest}
                  disabled={confirmationLoading}
                  className="w-40 rounded-xl bg-green-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
                >
                  {confirmationLoading
                    ? 'Processing...'
                    : canUnpauseDirectly
                    ? 'Unpause'
                    : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={submitUnpauseRequest}
          title={
            canUnpauseDirectly ? 'Confirm Unpause' : 'Confirm Unpause Request'
          }
          message={
            canUnpauseDirectly
              ? `Are you sure you want to unpause placement for ${student?.name}?`
              : `Are you sure you want to request unpausing placement for ${student?.name}?`
          }
          confirmText={
            canUnpauseDirectly ? 'Yes, Unpause' : 'Yes, Submit Request'
          }
          cancelText="Cancel"
          icon="question"
          disableConfirm={confirmationLoading}
        />
      </>
    );
  }

  return (
    <button
      onClick={() => {
        setSelectedStudent(student);
        setShowPauseModal(true);
      }}
      disabled={loadingStatus}
      className="relative min-h-8 min-w-32 rounded bg-yellow-500 px-3 py-1 text-white hover:bg-yellow-600"
    >
      {loadingStatus ? 'Loading..' : 'Pause Placement'}
    </button>
  );
};

export default PlacementStatusButton;
